import React, { useCallback, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { DicomRow, MediaUrls } from 'services/exploreService';
import './explore.css';

export const fmt = (n: number | null | undefined) =>
  n === null || n === undefined || Number.isNaN(n) ? '–' : Number(n).toLocaleString('en-US');

export const pct = (n: number, d: number) => (d > 0 ? `${((100 * n) / d).toFixed(d > 0 && n / d < 0.1 ? 1 : 0)}%` : '–');

export const viewerHref = (icid: string) => `/dicoms/${encodeURIComponent(icid)}`;
export const studyHref = (key: string) => `/studies/${encodeURIComponent(key)}`;

// ------------------------------------------------------------------ layout

export const PageHead: React.FC<{
  eyebrow?: React.ReactNode;
  title: React.ReactNode;
  lede?: React.ReactNode;
  actions?: React.ReactNode;
}> = ({ eyebrow, title, lede, actions }) => (
  <header className="ax-pagehead">
    <div>
      {eyebrow && <div className="ax-eyebrow">{eyebrow}</div>}
      <h1>{title}</h1>
      {lede && <p>{lede}</p>}
    </div>
    {actions && <div className="ax-actions">{actions}</div>}
  </header>
);

export const Panel: React.FC<{
  eyebrow?: React.ReactNode;
  title?: React.ReactNode;
  sub?: React.ReactNode;
  actions?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}> = ({ eyebrow, title, sub, actions, children, className }) => (
  <section className={`ax-panel ${className ?? ''}`}>
    {(eyebrow || title || actions) && (
      <div className="ax-panel-head">
        <div>
          {eyebrow && <div className="ax-eyebrow">{eyebrow}</div>}
          {title && <h2>{title}</h2>}
          {sub && <p>{sub}</p>}
        </div>
        {actions}
      </div>
    )}
    {children}
  </section>
);

export const Kpi: React.FC<{
  value: React.ReactNode;
  label: React.ReactNode;
  sub?: React.ReactNode;
  color?: string;
  onClick?: () => void;
}> = ({ value, label, sub, color, onClick }) => {
  const style = { ['--c' as any]: color } as React.CSSProperties;
  const body = (
    <>
      <span className="v">{value}</span>
      <span className="l">{label}</span>
      {sub && <span className="s">{sub}</span>}
    </>
  );
  return onClick ? (
    <button type="button" className="ax-tile" style={style} onClick={onClick}>
      {body}
    </button>
  ) : (
    <div className="ax-tile" style={style}>
      {body}
    </div>
  );
};

export const Skeleton: React.FC<{ h?: number; w?: string | number }> = ({ h = 14, w = '100%' }) => (
  <div className="ax-skel" style={{ height: h, width: w }} aria-hidden />
);

export const Empty: React.FC<{ title: string; children?: React.ReactNode }> = ({ title, children }) => (
  <div className="ax-empty" role="status">
    <b>{title}</b>
    {children}
  </div>
);

// ---------------------------------------------------------------- tooltips

interface TipState {
  x: number;
  y: number;
  content: React.ReactNode;
}

/** A small cursor-following tooltip shared by the chart components. */
export function useTip() {
  const [tip, setTip] = useState<TipState | null>(null);
  const show = useCallback((e: React.MouseEvent, content: React.ReactNode) => {
    const x = Math.min(e.clientX + 14, window.innerWidth - 290);
    setTip({ x, y: e.clientY + 14, content });
  }, []);
  const hide = useCallback(() => setTip(null), []);
  const node = tip ? (
    <div className="ax-tip" style={{ left: tip.x, top: tip.y }} role="tooltip">
      {tip.content}
    </div>
  ) : null;
  return { show, hide, node };
}

// ------------------------------------------------------------------ charts

export interface BarItem {
  key: string;
  label: React.ReactNode;
  hint?: React.ReactNode;
  value: number;
  secondary?: React.ReactNode;
  onClick?: () => void;
  tip?: React.ReactNode;
}

/** Single-series horizontal bars: one hue, a value on every row, clickable rows drill down. */
export const BarList: React.FC<{ items: BarItem[]; color?: string; max?: number; ariaLabel?: string }> = ({
  items,
  color = 'var(--s1)',
  max,
  ariaLabel,
}) => {
  const top = max ?? Math.max(1, ...items.map(i => i.value));
  const { show, hide, node } = useTip();
  return (
    <div className="ax-bars" role="list" aria-label={ariaLabel}>
      {items.map(i => {
        const content = (
          <>
            <span className="name" title={typeof i.label === 'string' ? i.label : undefined}>
              {i.label}
              {i.hint && <small>{i.hint}</small>}
            </span>
            <span className="ax-bar-track" aria-hidden>
              <i
                className="ax-bar-fill"
                style={{ width: `${(100 * i.value) / top}%`, ['--c' as any]: color }}
              />
            </span>
            <span className="val">
              {fmt(i.value)}
              {i.secondary && <small>{i.secondary}</small>}
            </span>
          </>
        );
        const hover = i.tip
          ? { onMouseMove: (e: React.MouseEvent) => show(e, i.tip), onMouseLeave: hide }
          : {};
        return i.onClick ? (
          <button key={i.key} type="button" className="ax-bar-row" role="listitem" onClick={i.onClick} {...hover}>
            {content}
          </button>
        ) : (
          <div key={i.key} className="ax-bar-row" role="listitem" {...hover}>
            {content}
          </div>
        );
      })}
      {node}
    </div>
  );
};

/** Two series per row (AI prediction vs human label), legend above, values on the right. */
export const PairedBars: React.FC<{
  rows: { key: string; label: string; a: number; b: number; onClick?: () => void }[];
  aName: string;
  bName: string;
}> = ({ rows, aName, bName }) => {
  const top = Math.max(1, ...rows.flatMap(r => [r.a, r.b]));
  const { show, hide, node } = useTip();
  return (
    <>
      <div className="ax-legend">
        <span>
          <i style={{ ['--c' as any]: 'var(--s1)' }} />
          {aName}
        </span>
        <span>
          <i style={{ ['--c' as any]: 'var(--s2)' }} />
          {bName}
        </span>
      </div>
      <div className="ax-bars" role="list">
        {rows.map(r => (
          <button
            key={r.key}
            type="button"
            className="ax-bar-row"
            role="listitem"
            onClick={r.onClick}
            onMouseMove={e =>
              show(
                e,
                <>
                  <b>{r.label}</b>
                  {aName}: {fmt(r.a)}
                  <br />
                  {bName}: {fmt(r.b)}
                </>
              )
            }
            onMouseLeave={hide}
          >
            <span className="name">{r.label}</span>
            <span className="ax-bar-track pair" aria-hidden>
              <i className="ax-bar-fill" style={{ width: `${(100 * r.a) / top}%`, ['--c' as any]: 'var(--s1)' }} />
              <i className="ax-bar-fill" style={{ width: `${(100 * r.b) / top}%`, ['--c' as any]: 'var(--s2)' }} />
            </span>
            <span className="val">
              {fmt(r.a)}
              <small>{fmt(r.b)}</small>
            </span>
          </button>
        ))}
      </div>
      {node}
    </>
  );
};

/** Vertical columns for an ordered category (visit year). */
export const Columns: React.FC<{
  items: { key: string; label: string; value: number; onClick?: () => void }[];
  unit: string;
}> = ({ items, unit }) => {
  const top = Math.max(1, ...items.map(i => i.value));
  const { show, hide, node } = useTip();
  if (!items.length) return <Empty title="No data" />;
  return (
    <div>
      <div className="ax-cols" role="list">
        {items.map(i => (
          <div
            key={i.key}
            className="ax-col"
            role="listitem"
            aria-label={`${i.label}: ${fmt(i.value)} ${unit}`}
            onClick={i.onClick}
            style={{ cursor: i.onClick ? 'pointer' : 'default' }}
            onMouseMove={e =>
              show(
                e,
                <>
                  <b>{i.label}</b>
                  {fmt(i.value)} {unit}
                </>
              )
            }
            onMouseLeave={hide}
          >
            <i style={{ height: `${(100 * i.value) / top}%` }} />
          </div>
        ))}
      </div>
      <div className="ax-col-axis">
        <span>{items[0].label}</span>
        {items.length > 2 && <span>{items[Math.floor(items.length / 2)].label}</span>}
        <span>{items[items.length - 1].label}</span>
      </div>
      {node}
    </div>
  );
};

export const Meter: React.FC<{ n: number; total: number; color?: string; title?: string }> = ({
  n,
  total,
  color,
  title,
}) => (
  <span className={`ax-meter ${n === 0 ? 'zero' : ''}`} title={title}>
    <span className="track" aria-hidden>
      <i style={{ width: `${total ? (100 * n) / total : 0}%`, ['--c' as any]: color }} />
    </span>
    {n}/{total}
  </span>
);

// ------------------------------------------------------------------- media

/**
 * Poster frame for a DICOM. Shows the first extracted frame, falls back to the still
 * render, and plays the cine loop while hovered. Missing objects degrade to a label.
 */
export const MediaThumb: React.FC<{
  media: MediaUrls;
  frames?: number | null;
  badge?: React.ReactNode;
  badgeRight?: React.ReactNode;
  marks?: string[];
  alt: string;
}> = ({ media, frames, badge, badgeRight, marks, alt }) => {
  const sources = [media.poster, media.still].filter(Boolean) as string[];
  const [srcIdx, setSrcIdx] = useState(0);
  const [hover, setHover] = useState(false);
  const [cineFailed, setCineFailed] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const isCine = (frames ?? 0) > 1 && !!media.cine && !cineFailed;
  const src = sources[srcIdx];

  return (
    <div
      className="ax-thumb"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {src ? (
        <img
          src={src}
          alt={alt}
          loading="lazy"
          decoding="async"
          referrerPolicy="no-referrer"
          onError={() => setSrcIdx(i => i + 1)}
        />
      ) : (
        <div className="ph">no preview</div>
      )}
      {hover && isCine && (
        <video
          ref={videoRef}
          src={media.cine!}
          autoPlay
          muted
          loop
          playsInline
          onError={() => setCineFailed(true)}
        />
      )}
      {badge && <span className="badge">{badge}</span>}
      {badgeRight && <span className="badge r">{badgeRight}</span>}
      {marks && marks.length > 0 && (
        <span className="marks">
          {marks.map(m => (
            <b key={m}>{m}</b>
          ))}
        </span>
      )}
    </div>
  );
};

export const ViewChips: React.FC<{ predicted?: string | null; labeled?: string | null; confidence?: number | null }> = ({
  predicted,
  labeled,
  confidence,
}) => {
  const mismatch = !!predicted && !!labeled && predicted !== labeled;
  return (
    <span className="chips">
      {predicted && (
        <span
          className={`ax-pill ai ${mismatch ? 'mismatch' : ''}`}
          title={`AI view prediction${confidence != null ? ` (${(confidence * 100).toFixed(0)}%)` : ''}`}
        >
          AI {predicted}
        </span>
      )}
      {labeled && (
        <span className={`ax-pill human ${mismatch ? 'mismatch' : ''}`} title="Human view label">
          Tech {labeled}
        </span>
      )}
      {!predicted && !labeled && <span className="ax-pill">no view</span>}
    </span>
  );
};

export const annotationMarks = (d: Pick<DicomRow, 'has_segmentation' | 'has_keypoints' | 'has_quality_label'>) =>
  [d.has_segmentation && 'SEG', d.has_keypoints && 'KP', d.has_quality_label && 'Q'].filter(Boolean) as string[];

/** Gallery card used on the DICOM browser and study page; opens the inspector when onOpen is given. */
export const DicomCard: React.FC<{ d: DicomRow; showStudy?: boolean; onOpen?: () => void }> = ({ d, showStudy, onOpen }) => {
  const body = (
    <>
      <MediaThumb
        media={d.media}
        frames={d.number_of_frames}
        alt={`First frame of ${d.icid}`}
        badge={d.index != null ? `#${d.index}` : undefined}
        badgeRight={d.number_of_frames && d.number_of_frames > 1 ? `${d.number_of_frames}f` : 'still'}
        marks={annotationMarks(d)}
      />
      <div className="meta">
        <div className="row">
          <span className="id">{d.icid}</span>
          {d.type && <span className="ax-pill">{d.type}</span>}
        </div>
        <ViewChips predicted={d.predicted_view} labeled={d.labeled_view} confidence={d.predicted_confidence} />
        <span className="sub">
          {[d.manufacturer, d.model].filter(Boolean).join(' · ') || 'Unknown scanner'}
          {showStudy && d.study_icid ? ` · ${d.study_icid}` : ''}
        </span>
      </div>
    </>
  );
  return onOpen ? (
    <button type="button" className="ax-card" onClick={onOpen} aria-label={`Inspect ${d.icid}`}>
      {body}
    </button>
  ) : (
    <Link to={viewerHref(d.icid)} className="ax-card" aria-label={`Open ${d.icid} in the viewer`}>
      {body}
    </Link>
  );
};
